const PROCESS_DEFINITIONS = [
  {
    key: 'APPLICATION_REVIEW',
    stageKey: 'Application Review',
    label: 'Application Review',
    color: '#2563eb',
    taskCodes: ['APPLICATION_REVIEW', 'ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER'],
  },
  {
    key: 'SCHEDULING',
    stageKey: 'Scheduling',
    label: 'Scheduling',
    color: '#0f8f83',
    taskCodes: ['AUDIT_SCHEDULING', 'ASSIGN_NEXT_PROCESS_SALES', 'ASSIGN_NEXT_PROCESS_SCHEDULER'],
    scheduleStatuses: ['Booked', 'In progress', 'CL Sent', 'Confirmed'],
  },
  {
    key: 'AUDIT',
    stageKey: 'Audit',
    label: 'Audit',
    color: '#ea580c',
    taskCodes: ['AUDIT', 'AUDIT_PLAN', 'AUDIT_REPORT', 'CORRECTIVE_ACTION'],
  },
  {
    key: 'TECHNICAL_REVIEW',
    stageKey: 'Technical Review',
    label: 'Technical Review',
    color: '#7c3aed',
    taskCodes: ['TECHNICAL_REVIEW', 'ASSIGN_NEXT_PROCESS_TECHNICAL', 'ASSIGN_NEXT_PROCESS_TECHNICAL_REVIEWER'],
  },
  {
    key: 'CERTIFICATE',
    stageKey: 'Certificate',
    label: 'Certificate Issuance',
    color: '#d97706',
    taskCodes: ['CERTIFICATE_DECISION', 'CERTIFICATE_ISSUANCE', 'ISSUE_CERTIFICATE', 'ASSIGN_NEXT_PROCESS_CERTIFICATION_AUTHORITY', 'ASSIGN_NEXT_PROCESS_CERTIFICATE_ISSUER'],
  },
];

const DONE_STATUSES = new Set(['DONE', 'COMPLETED', 'CLOSED']);
const DEFAULT_TASK_STATUS_KEYS = ['OPEN', 'IN_PROGRESS', 'NEED_REVISION', 'REJECTED', 'DONE'];

const upper = (value) => String(value || '').trim().toUpperCase();

const toStatusLabel = (value) => String(value || '')
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/^\w/, (letter) => letter.toUpperCase());

const getTaskStatus = (value) => {
  const normalized = upper(value).replace(/[ -]+/g, '_');
  if (DONE_STATUSES.has(normalized)) return { key: 'DONE', label: 'Done', filterStatus: 'DONE' };
  if (normalized === 'OPEN') return { key: 'OPEN', label: 'Open', filterStatus: 'OPEN' };
  if (normalized === 'IN_PROGRESS') return { key: 'IN_PROGRESS', label: 'In progress', filterStatus: 'IN_PROGRESS' };
  if (['NEED_REVISION', 'REVISION_REQUIRED'].includes(normalized)) return { key: 'NEED_REVISION', label: 'Need revision', filterStatus: 'NEED_REVISION' };
  if (['REJECTED', 'REJECT'].includes(normalized)) return { key: 'REJECTED', label: 'Rejected', filterStatus: 'REJECTED' };
  if (['CANCELLED', 'CANCELED'].includes(normalized)) return { key: 'CANCELLED', label: 'Cancelled', filterStatus: 'CANCELLED' };
  if (!normalized) return { key: 'UNSPECIFIED', label: 'Status not set', filterStatus: 'UNSPECIFIED' };
  return { key: normalized, label: toStatusLabel(normalized), filterStatus: normalized };
};

const scheduleStatusLabel = (value) => {
  const normalized = upper(value).replace(/[_-]/g, ' ');
  if (normalized === 'BOOKED') return 'Booked';
  if (normalized === 'IN PROGRESS' || normalized === 'OPEN') return 'In progress';
  if (normalized === 'CL SENT') return 'CL Sent';
  if (normalized === 'CONFIRMED') return 'Confirmed';
  return normalized ? String(value).trim() : 'Booked';
};

const assigneeName = (task) => {
  const direct = task?.assignedFullName || task?.assignedName || task?.assignedUsername || task?.assignedToName;
  return String(direct || '').trim();
};

const buildAssigneeWorkload = (tasks) => {
  const counts = new Map();
  tasks.forEach((task) => {
    const name = assigneeName(task);
    if (name) counts.set(name, (counts.get(name) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([name, taskCount]) => ({ name, taskCount }))
    .sort((left, right) => right.taskCount - left.taskCount || left.name.localeCompare(right.name));
};

const buildTaskStatuses = (tasks) => {
  const statusByKey = new Map();
  tasks.forEach((task) => {
    const status = getTaskStatus(task?.status);
    const current = statusByKey.get(status.key) || { ...status, value: 0 };
    current.value += 1;
    statusByKey.set(status.key, current);
  });

  const orderedKeys = [
    ...DEFAULT_TASK_STATUS_KEYS,
    ...[...statusByKey.keys()].filter((key) => !DEFAULT_TASK_STATUS_KEYS.includes(key)).sort(),
  ];

  return orderedKeys
    .map((key) => statusByKey.get(key) || { ...getTaskStatus(key), value: 0 })
    .filter((status) => status.value > 0 || ['OPEN', 'IN_PROGRESS', 'DONE'].includes(status.key));
};

const buildApplicationReviewStatuses = (tasks) => {
  const decision = (task) => upper(task?.reviewDecision);
  const statuses = [
    {
      label: 'In progress / Open',
      value: tasks.filter((task) => !decision(task) && !DONE_STATUSES.has(upper(task?.status))).length,
      filterStatus: 'OPEN',
    },
    { label: 'Approved', value: tasks.filter((task) => decision(task) === 'APPROVED').length, filterStatus: 'DONE' },
    { label: 'Approved with Notes', value: tasks.filter((task) => decision(task) === 'APPROVED_WITH_NOTES').length, filterStatus: 'DONE' },
    { label: 'Reject', value: tasks.filter((task) => ['REJECT', 'REJECTED'].includes(decision(task))).length, filterStatus: 'OPEN' },
  ];
  const completedWithoutDecision = tasks.filter((task) => !decision(task) && DONE_STATUSES.has(upper(task?.status))).length;
  if (completedWithoutDecision > 0) {
    statuses.push({ label: 'Completed without decision', value: completedWithoutDecision, filterStatus: 'DONE' });
  }
  return statuses;
};

const buildScheduleStatuses = (schedules, expectedStatuses) => {
  const auditSchedules = (Array.isArray(schedules) ? schedules : []).filter((schedule) => schedule?.scheduleKind === 'audit');
  const actual = auditSchedules.reduce((result, schedule) => {
    const label = scheduleStatusLabel(schedule?.scheduleStatus);
    result[label] = (result[label] || 0) + 1;
    return result;
  }, {});
  return expectedStatuses.map((label) => ({ label, value: actual[label] || 0, filterStatus: label.toUpperCase().replace(/\s+/g, '_') }));
};

export const buildAuditPipelineProcessInsights = (tasks = [], schedules = [], stageCounts = {}) => PROCESS_DEFINITIONS.map((definition) => {
  const processTasks = (Array.isArray(tasks) ? tasks : []).filter((task) => definition.taskCodes.includes(upper(task?.taskTypeCode)));
  const statuses = definition.key === 'APPLICATION_REVIEW'
    ? buildApplicationReviewStatuses(processTasks)
    : definition.key === 'SCHEDULING'
      ? buildScheduleStatuses(schedules, definition.scheduleStatuses)
      : buildTaskStatuses(processTasks);

  const assigneeWorkload = buildAssigneeWorkload(processTasks);
  return {
    ...definition,
    total: statuses.reduce((sum, status) => sum + status.value, 0),
    currentStageProjects: Number(stageCounts?.[definition.stageKey]) || 0,
    statuses,
    assignees: assigneeWorkload.map((item) => item.name),
    assigneeWorkload,
  };
});

const COMPLETED_WORKLOAD_LABELS = new Set([
  'Done',
  'Approved',
  'Approved with Notes',
  'Completed without decision',
  'Confirmed',
  'CL Sent',
  'Booked',
]);

const BOTTLENECK_PRIORITY = new Map([
  ['Need revision', 0],
  ['Rejected', 1],
  ['Reject', 1],
  ['Open', 2],
  ['In progress / Open', 2],
  ['In progress', 3],
]);

export const buildAuditPipelineStageFocus = (insights = [], selectedStage) => {
  const list = Array.isArray(insights) ? insights : [];
  const focusedProcess = list.find((process) => process.stageKey === selectedStage);
  if (!focusedProcess) return null;

  const candidateStatuses = focusedProcess.statuses
    .filter((status) => status.value > 0 && !COMPLETED_WORKLOAD_LABELS.has(status.label))
    .sort((left, right) => (
      right.value - left.value
      || (BOTTLENECK_PRIORITY.get(left.label) ?? 99) - (BOTTLENECK_PRIORITY.get(right.label) ?? 99)
      || left.label.localeCompare(right.label)
    ));
  const owners = new Set(list.flatMap((process) => process.assignees));

  return {
    stage: selectedStage,
    projectCount: focusedProcess.currentStageProjects,
    workloadTotal: list.reduce((sum, process) => sum + process.total, 0),
    ownerCount: owners.size,
    bottleneck: candidateStatuses[0]
      ? { label: candidateStatuses[0].label, value: candidateStatuses[0].value }
      : null,
  };
};
export const buildAssigneeAuditTaskHref = (assignee) => `/task?tab=audit-task&auditAssignee=${encodeURIComponent(String(assignee || '').trim())}`;
